import update from "immutability-helper";
import keyBy from "lodash/keyBy";
import uniqBy from "lodash/uniqBy";

import {
  AttachmentActionTypes,
  DELETE_ATTACHMENT_SUCCESS,
  GET_ATTACHMENTS_SUCCESS,
  UPDATE_ATTACHMENT_SUCCESS,
  UPLOAD_ATTACHMENT_ABORT,
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_PROGRESS_UPDATE,
  UPLOAD_ATTACHMENT_REQUEST,
  UPLOAD_ATTACHMENT_SUCCESS,
  UPLOAD_ATTACHMENTS_REQUEST,
} from "fond/redux/attachments";
import { Attachment, AttachmentEntityType, AttachmentsState } from "fond/types";
import { reduceReducers } from "fond/utils";
import { pageNumberForPaginationSet } from "fond/utils/pagination";

const initialState: AttachmentsState = {
  isOpen: false,
  items: {
    Feature: null,
    Project: null,
  },
  meta: {
    Feature: {
      entityId: null,
      fetchedPages: [],
    },
    Project: {
      entityId: null,
      fetchedPages: [],
    },
  },
  uploadStatus: {},
};

const findAttachmentEntitytype = (state: AttachmentsState, attachmentId: string): AttachmentEntityType => {
  if (!state.items.Feature && !state.items.Project) {
    return "Project";
  }
  if (state.items.Feature?.find((item) => item.ID === attachmentId)) {
    return "Feature";
  }
  return "Project";
};

/**
 * filter out the attachments based on the entity type and entity id - this allows building up the list of attachments for a specific entity
 * while paginating through the set and clearing the list when a new entity is selected.
 * @param attachments
 * @param entityType
 * @param entityId
 * @returns
 */
const filterAttachmentsByEntityId = (attachments: Attachment[], entityType: AttachmentEntityType, entityId: string) => {
  return attachments.filter((attachment) => {
    return entityType === "Project" ? attachment.ProjectID === entityId : attachment.LayerFeature?.ID === entityId;
  });
};

/**
 * Determine if the request is for the same contiguous dataset.
 *
 * The dataset is considered contiguous if:
 * - the entity id is the same
 * - the sort model is the same.
 * - the pagination limit is the same
 *
 * If any of these change, the dataset is considered different and we will reset our pagination and re-fetch data.
 *
 * @param state
 * @param action
 */
const isSameDataset = (state: AttachmentsState, action: AttachmentActionTypes.GetAttachmentsSuccess) => {
  const meta = state.meta[action.entityType];
  if (!meta) {
    return false;
  }
  const isSameEntity = meta.entityId === action.entityId;
  const isSamePaginationLimit = meta.limit === action.payload.Pagination.Limit;
  const isSameSortModel = JSON.stringify(meta.sortModel) === JSON.stringify(action.sortModel);
  const isSameFilterType = meta.filterType === action.filterType;
  const isSameVersionIds = JSON.stringify(meta.versionIds) === JSON.stringify(action.versionIds);

  return isSameEntity && isSameSortModel && isSamePaginationLimit && isSameFilterType && isSameVersionIds;
};

export const attachmentsReducer = reduceReducers((state = initialState, action: AttachmentActionTypes.ReducerActions): AttachmentsState => {
  switch (action.type) {
    case GET_ATTACHMENTS_SUCCESS: {
      const entityKey = action.entityType ?? "Project";
      const updatedAttachmentsSet = uniqBy([...(state.items[entityKey] || []), ...action.payload.Attachments], "ID");
      const thisPageNumber = pageNumberForPaginationSet(action.payload.Pagination);

      return update(state, {
        items: {
          $set: {
            ...state.items,
            [entityKey]: action.entityId ? filterAttachmentsByEntityId(updatedAttachmentsSet, entityKey, action.entityId) : updatedAttachmentsSet,
          },
        },
        meta: {
          $set: {
            ...state.meta,
            [entityKey]: {
              entityId: action.entityId,
              // when we are requesting the same dataset, append the requested page to the fetched pages
              // otherwise, reset the fetched pages to the current page
              // this allows us to keep the fetched pages in sync with the current page
              // and avoid re-fetching the same pages
              fetchedPages: isSameDataset(state, action) ? [...state.meta[entityKey]!.fetchedPages, thisPageNumber] : [thisPageNumber],
              total: action.payload.Pagination.Total,
              limit: action.payload.Pagination.Limit,
              sortModel: action.sortModel,
              filterType: action.filterType,
              versionIds: action.versionIds,
            },
          },
        },
        uploadStatus: { $set: {} },
      });
    }
    case UPDATE_ATTACHMENT_SUCCESS: {
      const entityKey = findAttachmentEntitytype(state, action.attachment.ID);
      return update(state, {
        items: {
          $set: {
            ...state.items,
            [entityKey]: [...(state.items[entityKey]?.filter((item) => item.ID !== action.attachment.ID) || []), action.attachment],
          },
        },
        meta: {
          $set: {
            ...state.meta,
            [entityKey]: {
              // reset the fetched pages so that the attachments are re-fetched
              entityId: undefined,
              fetchedPages: [],
            },
          },
        },
      });
    }
    case DELETE_ATTACHMENT_SUCCESS: {
      const entityKey = findAttachmentEntitytype(state, action.attachment.ID);
      const currentTotal = state.meta[entityKey]?.total ?? 0;
      return update(state, {
        items: {
          $set: {
            ...state.items,
            [entityKey]: [...(state.items[entityKey]?.filter((item) => item.ID !== action.attachment.ID) || [])],
          },
        },
        meta: {
          $set: {
            ...state.meta,
            [entityKey]: {
              total: currentTotal ? currentTotal - 1 : 0,
            },
          },
        },
      });
    }
    case UPLOAD_ATTACHMENTS_REQUEST: {
      return update(state, {
        uploadStatus: {
          $merge: keyBy(
            action.files.map((file) => ({
              name: file.name,
              progress: 0,
              isError: false,
              isAborted: false,
              request: null,
            })),
            "name"
          ),
        },
      });
    }
    case UPLOAD_ATTACHMENT_REQUEST:
      return update(state, {
        uploadStatus: {
          [action.file.name]: {
            request: { $set: action.XHRRequest },
          },
        },
      });
    case UPLOAD_ATTACHMENT_SUCCESS: {
      const entityKey = findAttachmentEntitytype(state, action.attachment.ID);
      return update(state, {
        meta: {
          $set: {
            ...state.meta,
            [entityKey]: {
              // reset the fetched pages so that the attachments are re-fetched
              entityId: undefined,
              fetchedPages: [],
            },
          },
        },
      });
    }
    case UPLOAD_ATTACHMENT_FAILURE:
      return update(state, {
        uploadStatus: {
          [action.file.name]: {
            isError: { $set: true },
            error: { $set: action.error },
          },
        },
      });
    case UPLOAD_ATTACHMENT_PROGRESS_UPDATE:
      return update(state, {
        uploadStatus: {
          [action.file.name]: {
            progress: { $set: action.percentComplete },
          },
        },
      });
    case UPLOAD_ATTACHMENT_ABORT:
      return update(state, {
        uploadStatus: {
          [action.file.name]: {
            isAborted: { $set: true },
          },
        },
      });
    default:
      return state;
  }
});
